import CustomLoader from "@/components/CustomLoader/customLoader.component";
import { Button } from "@/shadcn/components/ui/button";
import { motion, useAnimation } from "framer-motion";
import { Square, SquareCheckBig } from "lucide-react";
import { memo, useCallback, useState } from "react";

type TaskStatusButtonProps = {
  taskID: number;
  status: string | null | undefined;
  handleStatusUpdate: (
    taskID: number,
    status: "complete" | "incomplete",
    subTask: boolean
  ) => Promise<void>;
};

const areEqual = (
  prevProps: TaskStatusButtonProps,
  nextProps: TaskStatusButtonProps
) => {
  return (
    prevProps.status === nextProps.status &&
    prevProps.taskID === nextProps.taskID
  );
};

const TaskStatusButton: React.FC<TaskStatusButtonProps> = ({
  taskID,
  status,
  handleStatusUpdate,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const externalIconControls = useAnimation();

  const handleHoverStart = useCallback(() => {
    externalIconControls.start("hover");
  }, [externalIconControls]);

  const handleHoverEnd = useCallback(() => {
    externalIconControls.start("rest");
  }, [externalIconControls]);

  // Square
  const iconVariants = {
    rest: {
      opacity: status === "complete" ? 0 : 1,
      scale: 1,
      transition: { type: "ease", duration: 0.4 },
    },
    hover: {
      opacity: status === "complete" ? 1 : 0,
      scale: 1,
      transition: { type: "ease", duration: 0.3 },
    },
  };

  // Checkbox
  const checkIconVariants = {
    rest: {
      opacity: status === "complete" ? 1 : 0,
      scale: 1,
      transition: { type: "ease", duration: 0.4 },
    },
    hover: {
      opacity: status === "complete" ? 0 : 1,
      scale: 1,
      transition: { type: "ease", duration: 0.3 },
    },
  };

  const MotionButton = motion.create(Button);

  // If no valid status is provided, show a disabled button.
  if (!status) {
    return (
      <Button variant="outline" size="sm" disabled>
        Invalid Task Status
      </Button>
    );
  }

  return (
    <MotionButton
      variant="outline"
      size="sm"
      onClick={async () => {
        setSubmitting(true);
        await handleStatusUpdate(
          taskID,
          status === "complete" ? "incomplete" : "complete",
          false
        );
        setSubmitting(false);
      }}
      disabled={submitting}
      onHoverStart={handleHoverStart}
      onHoverEnd={handleHoverEnd}
      className={`btn btn-primary rounded-sm ${
        status === "complete"
          ? "text-gray-500 hover:bg-gray-700 hover:text-white"
          : "dark:text-white hover:bg-green-500 hover:text-white"
      }`}
    >
      <div className="flex items-center relative">
        {!submitting ? (
          <>
            <motion.div
              animate={externalIconControls}
              variants={iconVariants}
              className="absolute"
              initial={status === "complete" ? "rest" : undefined}
            >
              <Square className="mr-1" />
            </motion.div>
            <motion.div
              animate={externalIconControls}
              variants={checkIconVariants}
              className="absolute"
              initial={status === "incomplete" ? "rest" : undefined}
            >
              <SquareCheckBig className="mr-1" />
            </motion.div>
          </>
        ) : (
          <div className="flex items-center relative">
            <CustomLoader size="xs" />
          </div>
        )}
        <span className={submitting ? "ms-1" : "ms-5"}>
          {status === "complete" ? "Mark Incomplete" : "Mark Complete"}
        </span>
      </div>
    </MotionButton>
  );
};

export default memo(TaskStatusButton, areEqual);
